import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import classnames from 'classnames';

import Loader from '../../../shared/Loader';
import { can } from '../../../components/Authorize';
import { ANALYTICS_VISION_READ } from '../permissions';
import { getCrowdCountData } from '../api';

import CountChart from './CountChart';
import AgeTimelineChart from './AgeTimelineChart';
import GenderGroupedBar from './GenderCharts';
import AveragesStrip from './AveragesStrip';
import { generatePlaceholderCrowdCountData } from './constants';

import visionStyle from '../VisionSection/style.module.scss';
import style from './style.module.scss';

const CrowdCountSection = () => {
  const slugDate = useSelector(({ events: { selectedEvents } }) => selectedEvents?.[0]?.slugDate);
  const [data, setData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  /** null = unknown; false = feature off / unavailable; true = show section */
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    if (!slugDate) {
      return undefined;
    }

    let cancelled = false;

    const loadCrowdCountData = async () => {
      setIsFetching(true);
      setEnabled(null);

      try {
        const response = await getCrowdCountData(slugDate);
        if (!cancelled) {
          // API: 204 → undefined when CROWD_COUNT_ENABLED=false; payload object when on.
          if (response == null) {
            setEnabled(false);
            setData(null);
          } else {
            setEnabled(true);
            setData(response);
          }
        }
      } catch {
        if (!cancelled) {
          setEnabled(false);
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setIsFetching(false);
        }
      }
    };

    loadCrowdCountData();

    return () => {
      cancelled = true;
    };
  }, [slugDate]);

  const hasData = Boolean(data?.hasData || data?.scanCount > 0);
  const hasPermission = can(ANALYTICS_VISION_READ);
  const showLive = hasPermission && hasData;
  const [placeholderData, setPlaceholderData] = useState(generatePlaceholderCrowdCountData);

  useEffect(() => {
    if (!isFetching && !showLive) {
      setPlaceholderData(generatePlaceholderCrowdCountData());
    }
  }, [slugDate, isFetching, showLive]);

  const chartData = showLive ? data : placeholderData;

  // Hidden until API confirms feature is on.
  if (enabled !== true) {
    return null;
  }

  return (
    <div className="mt-4 print-break-page">
      <div className={visionStyle.visionHeader}>
        <h4>Crowd Count</h4>
      </div>

      {isFetching && <Loader />}

      {!isFetching && (
        <div className={classnames({ [style.blurredPreview]: !showLive })}>
          <AveragesStrip data={chartData} />
          <Row className="mt-4">
            <Col>
              <CountChart data={chartData} />
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <AgeTimelineChart data={chartData} />
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <GenderGroupedBar data={chartData} />
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default CrowdCountSection;
